import dayjs from 'dayjs';
import { EnvironmentCustomerType } from '@mwe/environment';
import { Aktionscode, IFormModel, Product, TariffSelectionCardData, ZusatzInformationen } from '@mwe/models';
import { effect, untracked } from '@angular/core';
import { NewVoucherStoreType } from './new-voucher.store';
import { INewVoucherFormConfig, NewClientStoreType, NewVoucherProcessType, RelocationStoreType, VoucherStateService } from '@mwe/services';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TariffSelectionCardLogic } from '@mwe/ui/components';

/** ===== Types & Interfaces ===== **/

export type CodeStatus = 'check' | 'delete';

// valid -> at least one voucher is valid
// pending -> still to be concluded (necessary because of Wiener Linien vouchers)
// error -> all vouchers are invalid
// warning -> special case where voucher is invalid
export type CodeValidity = 'valid' | 'pending' | 'error' | 'warning';

// Wrapped inside an object so that the same result emits a new signal
// true -> valid
// false -> invalid
// undefined -> valid (but not existent)
// null -> currently being fetched
export type ValidityObject = { value: true | false | undefined | null };

export type ErrorType = number | string;

export type AktionsArt = '0' | '1' | '2';

export interface ILError {
  error: {
    code: string;
    message: string;
  };
}

export interface NewVoucherWarning {
  titleKey: string;
  messageKey: string;
  ctaLabelKey: string;
}

export enum NewVoucherIdentifier {
  VOUCHER_DETAIL = 'voucherDetail',
  VOUCHER_VALIDITY = 'voucherValidity',
}

/** ===== Constants ===== **/

export const CODE_CONTROL_NAME = 'mwe-new-voucher-code';
export const BIRTH_DATE_CONTROL_NAME = 'mwe-new-voucher-wl-birthdate';
export const CARD_NUMBER_CONTROL_NAME = 'mwe-new-voucher-wl-cardNumber';
export const COMMIT_CONTROL_NAME = 'mwe-new-voucher-wl-commit';

const DEFAULT_ERROR = 'newVoucher.input.error.default'; // TODO: Improve this

export const ERRORS: Record<ErrorType, string> = {
  default: DEFAULT_ERROR,
  required: 'newVoucher.input.error.required',
  404: 'newVoucher.input.error.required',
  500: DEFAULT_ERROR,
  3005: DEFAULT_ERROR,
  3006: DEFAULT_ERROR,
  3023: 'newVoucher.input.error.IL3023',
  3024: 'newVoucher.input.error.IL3024',
};

const DEFAULT_WL_ERROR = 'newVoucher.wl.inputCardNumber.error';

export const WL_ERRORS: Record<ErrorType, string> = {
  required: DEFAULT_WL_ERROR,
  pattern: DEFAULT_WL_ERROR,
  maxlength: DEFAULT_WL_ERROR,
  408: DEFAULT_ERROR,
  500: DEFAULT_ERROR,
  3005: DEFAULT_WL_ERROR,
  3017: 'newVoucher.input.error.IL3017',
  3019: 'newVoucher.input.error.IL3019',
  3024: 'newVoucher.input.error.IL3024',
};

/** ===== Utility Functions ===== **/

export function getSessionStorageName(environment: EnvironmentCustomerType = 'privat'): string {
  // returns the sessionStorage key of NewVoucherStore - if not set, then it is set to privat
  return `mwe_${environment}_new_voucher`;
}

interface NewVoucherEffectsInterface {
  state: VoucherStateService;
  store: NewClientStoreType | RelocationStoreType;
  newVoucherStore: NewVoucherStoreType;
  tariffSelectionCardLogic: TariffSelectionCardLogic;
}

export function newVoucherEffects({ state, store, newVoucherStore, tariffSelectionCardLogic }: Partial<NewVoucherEffectsInterface>): void {
  if (state && newVoucherStore) {
    effect(() => (state['voucherInfo'] = newVoucherStore.voucher()));
  }

  if (store && newVoucherStore) {
    effect(() => {
      const voucherInfo = newVoucherStore.voucher();
      untracked(() => store.updateStoreState({ voucherInfo }));
    });
  }

  if (newVoucherStore && tariffSelectionCardLogic) {
    effect(() => {
      newVoucherStore.preisindikationenAPICall(); // as to reduce the amount of requests, this custom signal is being reacted to instead of voucher()
      untracked(() => tariffSelectionCardLogic.handleVoucherCodeUpdate(newVoucherStore.voucher()?.aktionscode ?? ''));
    });
  }
}

export function initWithConfig(config: INewVoucherFormConfig): INewVoucherFormConfig {
  config.processType = config.processType ?? 'Kunde werden';
  config.predefinedCode = config.predefinedCode ?? config.campaignVoucher?.defaultValue ?? undefined;
  config.headingNodeType = config.headingNodeType ?? 'h2';

  return config;
}

export function createNewVoucherWarning(titleKey: string, messageKey: string, ctaLabelKey?: string): NewVoucherWarning {
  const WARNING_START = 'newVoucher.input.warning.';

  return {
    titleKey: WARNING_START + (titleKey ?? 'default'),
    messageKey: WARNING_START + (messageKey ?? 'default'),
    ctaLabelKey: ctaLabelKey ? WARNING_START + ctaLabelKey : undefined,
  };
}

export function createAdditionalFields(formData: IFormModel): ZusatzInformationen[] {
  const additionalFields: ZusatzInformationen[] = [];
  if (!formData?.[COMMIT_CONTROL_NAME]) return undefined;

  additionalFields.push({ feld: 'geburtsdatum', wert: formData?.[BIRTH_DATE_CONTROL_NAME] });
  additionalFields.push({ feld: 'jahreskartennummer', wert: formData?.[CARD_NUMBER_CONTROL_NAME] });

  return additionalFields;
}

interface CreateBodyInterface {
  config: INewVoucherFormConfig;
  additionalFields: ZusatzInformationen[];
  selectedTariff: TariffSelectionCardData;
  product: Product;
}

export function createBody({ config, additionalFields, selectedTariff, product }: Partial<CreateBodyInterface>): string {
  const body = {
    datum: dayjs().toISOString(),
    tarifTyp: selectedTariff ? selectedTariff.tariffKey : product?.tarifKey,
    prozessCode: config.processType,
    zusatzInformationen: additionalFields ?? [],
    gewaehlteTarifOptionen: selectedTariff?.userSelection,
  };

  return JSON.stringify(body);
}

export function createProcessILError(processType: NewVoucherProcessType, ILError: ErrorType): string {
  ILError = 'IL' + ILError.toString();

  switch (processType) {
    case 'Energiegutschein':
      ILError += 'EG';
      break;
    case 'Kunde werden':
      ILError += 'NEG';
      break;
    case 'Tarifwechsel':
      ILError += 'NEG';
      break;
    default:
      ILError = 'default';
  }

  return 'newVoucher.input.error.' + ILError;
}

export function capitalizeFirstLetter(string: string): string {
  if (!string) return string;
  if (string.length === 1) return string.toUpperCase();

  return string[0].toUpperCase() + string.slice(1);
}

export function deleteCodeStatus(codeStatus: CodeStatus): boolean {
  return codeStatus === 'delete';
}

export function checkCodeStatus(codeStatus: CodeStatus): boolean {
  return codeStatus === 'check';
}

export function isEnergiegutschein(newVoucherProcessType: NewVoucherProcessType): boolean {
  return newVoucherProcessType === 'Energiegutschein';
}

export function isTarifwechselOrKundeWerden(newVoucherProcessType: NewVoucherProcessType): boolean {
  return isTarifwechsel(newVoucherProcessType) || isKundeWerden(newVoucherProcessType);
}

export function isTarifwechsel(newVoucherProcessType: NewVoucherProcessType): boolean {
  return newVoucherProcessType === 'Tarifwechsel';
}

export function isKundeWerden(newVoucherProcessType: NewVoucherProcessType): boolean {
  return newVoucherProcessType === 'Kunde werden';
}

export function validCodeValidity(codeValidity: CodeValidity): boolean {
  return codeValidity === 'valid';
}

export function errorCodeValidity(codeValidity: CodeValidity): boolean {
  return !codeValidity || codeValidity === 'error';
}

export function isWienerLinienVoucher(voucherDetail: Aktionscode): boolean {
  const aktionsArt = voucherDetail?.aktion?.aktionsArt;
  return aktionsArt === '1' || aktionsArt === '2';
}

export function isDigitalWienerLinienVoucher(aktionsArt: AktionsArt | string): boolean {
  return aktionsArt === '2';
}
